"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { decodeJwtPayload, isTokenExpired } from "@/lib/auth";
import {
  getAccessToken,
  refreshSession,
  revokeSession,
} from "@/lib/token-store";
import { apiGet } from "@/lib/api";
import { subscribeAuth } from "@/lib/auth-broadcast";

interface OrgRole {
  org_id: string;
  role: string;
}

interface TmsOrgRole {
  tenant_id: string;
  tms_role: string;
}

interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  /** JWT-level system_role — use tms_system_role for TMS authorization */
  system_role: string;
  org_roles: OrgRole[];
  /** TMS-authoritative system role from employee record */
  tms_system_role: string | null;
  /** TMS org roles keyed by tenant_id */
  tms_org_roles: TmsOrgRole[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: async () => {},
  refresh: async () => false,
});

async function fetchTmsProfile(): Promise<{ system_role: string | null; org_roles: TmsOrgRole[] }> {
  try {
    const data = await apiGet<{ system_role?: string; org_roles?: TmsOrgRole[] }>("me/profile");
    return {
      system_role: data.system_role ?? null,
      org_roles: data.org_roles ?? [],
    };
  } catch {
    return { system_role: null, org_roles: [] };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  // The Portal View (/p/**, ADR 0002) runs under this same root layout but uses
  // its OWN passenger-auth (PortalAuthProvider + __portal_session). On those paths
  // this staff OAuth provider must do nothing — no /api/auth/refresh, no profile
  // fetch, no redirect — so it never tries to authenticate a portal visitor as staff.
  const isPortalPath = pathname?.startsWith("/p/") ?? false;
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (typeof window === "undefined") return;

    let token = getAccessToken();

    if (!token || isTokenExpired(token, 0)) {
      // Single-flight refresh (token-store) — safe against the concurrent
      // page-load race that used to kill the session (ADR 0052, W5).
      const refreshed = await refreshSession().catch(() => false);
      token = refreshed ? getAccessToken() : null;
      if (!token || isTokenExpired(token, 0)) {
        setUser(null);
        setLoading(false);
        return;
      }
    }

    const payload = decodeJwtPayload(token);
    if (payload) {
      const profile = await fetchTmsProfile();
      setUser({
        id: payload.sub as string,
        email: payload.email as string,
        display_name: payload.display_name as string,
        system_role: payload.system_role as string,
        org_roles: (payload.org_roles as OrgRole[]) || [],
        tms_system_role: profile.system_role,
        tms_org_roles: profile.org_roles,
      });
    }
    setLoading(false);
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    const ok = await refreshSession().catch(() => false);
    if (!ok) return false;
    loadUser();
    return true;
  }, [loadUser]);

  const logout = useCallback(async () => {
    await revokeSession();
    setUser(null);
    router.push("/login");
  }, [router]);

  useEffect(() => {
    if (isPortalPath) {
      setLoading(false);
      return;
    }
    loadUser();
  }, [loadUser, isPortalPath]);

  /**
   * ADR 0228 D3 — แท็บอื่นบอกมาว่ามีอะไรเปลี่ยน
   *
   * `refreshed` ทำให้ `tokenTick` ขยับ → effect ตั้งนาฬิกาข้างล่างรันใหม่แล้วอ่าน `exp`
   * ของ token ใบใหม่ ไม่งั้นแท็บนี้จะยังตื่นตามเวลาของ token ใบเก่า แล้วไปแย่งล็อกเปล่า ๆ
   *
   * `logout` ปิดช่องที่แท็บอื่นยังคิดว่าตัวเองล็อกอินอยู่จนกว่าจะยิง request แล้วเจอ 401
   */
  const [tokenTick, setTokenTick] = useState(0);
  useEffect(() => {
    if (isPortalPath) return;
    return subscribeAuth((msg) => {
      if (msg.type === "logout") {
        setUser(null);
        router.replace("/login");
        return;
      }
      setTokenTick((n) => n + 1);
    });
  }, [isPortalPath, router]);

  useEffect(() => {
    if (isPortalPath) return;
    if (!user) return;

    const token = getAccessToken();
    if (!token) return;

    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") return;

    /* ADR 0228 D8 — jitter สุ่ม 0–30 วิ
       ล็อกเป็นตัวกันหลัก อันนี้แค่ทำให้แท็บที่เปิดพร้อมกันไม่ตื่นพร้อมกันเป๊ะ
       ลดการเข้าคิวโดยไม่จำเป็น */
    const jitterMs = Math.floor(Math.random() * 30000);
    const msUntilRefresh = (payload.exp as number) * 1000 - Date.now() - 60000 - jitterMs;
    if (msUntilRefresh <= 0) {
      refresh();
      return;
    }

    const timer = setTimeout(() => {
      refresh();
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [user, refresh, isPortalPath, tokenTick]);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
